/**
 * Zustand store for the Simplify Developer Plans tab.
 * Holds server-side pagination/sort/filter state plus sheet state.
 */

import { create } from 'zustand';
import type { SimplifyDeveloperPlan } from '@/application/queries/simplifyDeveloperPlan';

export interface SimplifyPaginationState {
  page: number;
  pageSize: number;
}

export interface SimplifySortState {
  id: string;
  desc: boolean;
}

interface SimplifyDeveloperPlansState {
  // Data
  items: SimplifyDeveloperPlan[];
  totalCount: number;
  loading: boolean;
  error: Error | null;

  // Table controls
  pagination: SimplifyPaginationState;
  sorting: SimplifySortState[];
  searchText: string;

  // Sheet
  selectedItem: SimplifyDeveloperPlan | null;
  sheetOpen: boolean;
  extending: boolean;
  extendError: Error | null;
  extendSuccess: boolean;

  // Setters
  setData: (items: SimplifyDeveloperPlan[], totalCount: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setPagination: (pagination: SimplifyPaginationState) => void;
  setSorting: (sorting: SimplifySortState[]) => void;
  setSearchText: (searchText: string) => void;
  openSheet: (item: SimplifyDeveloperPlan) => void;
  closeSheet: () => void;
  setExtending: (extending: boolean) => void;
  setExtendError: (error: Error | null) => void;
  setExtendSuccess: (success: boolean) => void;
  updateItemTrialDate: (subscriptionId: string, newTrialEnd: string) => void;

  _reset: () => void;
}

const initialState = {
  items: [] as SimplifyDeveloperPlan[],
  totalCount: 0,
  loading: true,
  error: null as Error | null,
  pagination: { page: 1, pageSize: 20 } as SimplifyPaginationState,
  sorting: [] as SimplifySortState[],
  searchText: '',
  selectedItem: null as SimplifyDeveloperPlan | null,
  sheetOpen: false,
  extending: false,
  extendError: null as Error | null,
  extendSuccess: false,
};

export const useSimplifyDeveloperPlansStore = create<SimplifyDeveloperPlansState>((set) => ({
  ...initialState,

  setData: (items, totalCount) => set({ items, totalCount, loading: false, error: null }),
  setLoading: (loading) => set({ loading }),
  // Only set the field explicitly requested. Side-effects on `loading` cause
  // setError(null) to clobber a setLoading(true) that was just dispatched.
  setError: (error) => set({ error }),

  setPagination: (pagination) => set({ pagination }),
  setSorting: (sorting) => set({ sorting, pagination: { ...initialState.pagination } }),
  setSearchText: (searchText) =>
    set({ searchText, pagination: { ...initialState.pagination } }),

  openSheet: (selectedItem) =>
    set({ selectedItem, sheetOpen: true, extendError: null, extendSuccess: false }),
  closeSheet: () =>
    set({
      selectedItem: null,
      sheetOpen: false,
      extendError: null,
      extendSuccess: false,
    }),

  setExtending: (extending) => set({ extending }),
  setExtendError: (extendError) => set({ extendError }),
  setExtendSuccess: (extendSuccess) => set({ extendSuccess }),

  updateItemTrialDate: (subscriptionId, newTrialEnd) =>
    set((state) => ({
      items: state.items.map((it) =>
        it.subscriptionId === subscriptionId ? { ...it, trialEndDate: newTrialEnd } : it
      ),
      selectedItem:
        state.selectedItem?.subscriptionId === subscriptionId
          ? { ...state.selectedItem, trialEndDate: newTrialEnd }
          : state.selectedItem,
    })),

  _reset: () => set(initialState),
}));
